import {
  Box,
  Chip,
  Flex,
  Icon,
  Separator,
  Text,
  Tooltip,
  styled,
} from '@kandji-inc/nectar-ui';
import { getDeviceIcon } from 'src/features/library-items/pages/utils/device-os-helpers';
import { useGetEnabledPlatforms } from 'src/hooks/useGetEnabledPlatforms';
import { i18n } from 'src/i18n';
import {
  appleOnlyDevices,
  platformsFromDevices,
} from 'src/utils/PlatformUtils';

const DeviceWrapper = styled(Flex, {
  alignItems: 'center',
  gap: 2,
  color: '$text_secondary',
});

const TooltipHeader = styled(Text, {
  color: 'var(--Dropdown-dropdown-section-label, #7B8BA3)',
  fontSize: 'var(--font-size-H6, 12px)',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'var(--font-line-height-sm-16, 16px) /* 133.333% */',
});

const TooltipSeparator = styled(Separator, {
  margin: '6px 0',
});

const TooltipContainer = styled(Flex, {
  gap: 10,
  padding: '$2',
  flexDirection: 'column',
});

export const PlatformTooltipComponents = {
  Root: TooltipContainer,
  Header: TooltipHeader,
  Separator: TooltipSeparator,
};

export function PlatformChip({ label, name, chipProps = {}, ...rest }) {
  return (
    <Chip
      label={
        <>
          <Icon size="sm" name={name} {...rest} /> {label}
        </>
      }
      {...chipProps}
    />
  );
}

export function PlatformTooltip({ children, content }) {
  return (
    <Tooltip
      theme="light"
      content={content}
      css={{ maxWidth: 'unset', zIndex: 2000, width: 'max-content' }}
    >
      <Box>{children}</Box>
    </Tooltip>
  );
}

export function PlatformTooltipChips({ devices }) {
  const { hasApple, hasWindows, hasAndroid } = platformsFromDevices(devices);

  const appleDevices = appleOnlyDevices(devices);
  const deviceIcons = appleDevices.map((device, index) => (
    <DeviceWrapper key={device}>
      <Icon name={getDeviceIcon(device, true)} size="unset" /> {device}
      {index < appleDevices.length - 1 ? ', ' : ''}
    </DeviceWrapper>
  ));

  return (
    <>
      {hasApple && (
        <Flex flow="column" css={{ gap: 6 }}>
          <PlatformChip name="apple-logo" label={'Apple'} />
          <Flex css={{ gap: 4 }}>{deviceIcons}</Flex>
        </Flex>
      )}
      {(hasWindows || hasAndroid) && (
        <Flex css={{ gap: 6 }}>
          {hasWindows && <PlatformChip name="windows-logo" label={'Windows'} />}
          {hasAndroid && <PlatformChip name="android-logo" label={'Android'} />}
        </Flex>
      )}
    </>
  );
}

export function PlatformIcons({ devices, iconProps = {} }) {
  const { windowsEnabled, androidEnabled } = useGetEnabledPlatforms();
  const { hasApple, hasWindows, hasAndroid } = platformsFromDevices(devices);

  return (
    <Flex>
      {hasApple && <Icon size="sm" name="apple-logo" {...iconProps} />}
      {windowsEnabled && hasWindows && (
        <Icon size="sm" name="windows-logo" {...iconProps} />
      )}
      {androidEnabled && hasAndroid && (
        <Flex css={{ marginLeft: 2 }}>
          <Icon size="sm" name="android-logo" {...iconProps} />
        </Flex>
      )}
    </Flex>
  );
}

export function PlatformIconWrapper({
  devices,
  fallback,
  iconProps = {},
  tooltipContent,
}) {
  const { crossPlatformEnabled } = useGetEnabledPlatforms();

  if (!crossPlatformEnabled) {
    return fallback;
  }

  return (
    <PlatformTooltip content={tooltipContent}>
      <PlatformIcons devices={devices} iconProps={iconProps} />
    </PlatformTooltip>
  );
}
